import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Repeat2, ArrowLeftRight, Shuffle, Hash, GraduationCap, ArrowLeft } from "lucide-react";
import PageHeader from "../components/PageHeader";
import "../styles/vocabTestSetup.css";
import { YEAR_OPTIONS, DISPLAY_YEAR_OPTIONS, mapToNearestTier } from "../data/vocabYearGroups";

const TYPE_META = {
  definitions: { label: "Definitions Test", icon: <BookOpen size={20} />, color: "vts-indigo" },
  synonyms:    { label: "Synonyms Test",    icon: <Repeat2 size={20} />,      color: "vts-teal"   },
  antonyms:    { label: "Antonyms Test",    icon: <ArrowLeftRight size={20} />, color: "vts-orange" },
  mixed:       { label: "Mixed Test",       icon: <Shuffle size={20} />,       color: "vts-purple" },
};

const WORD_COUNTS = [10, 25, 50, 100];

export default function VocabTestSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const testType = location.state?.testType || "definitions";
  const meta = TYPE_META[testType] || TYPE_META.definitions;

  useEffect(() => { document.title = "Test Setup — Owllet Reading Quest"; }, []);

  const [wordCount, setWordCount] = useState(10);
  const [selectedTier, setSelectedTier] = useState(() => {
    const saved = localStorage.getItem("difficulty_tier");
    return mapToNearestTier(saved);
  });
  const [lockedTier] = useState(() =>
    !!(sessionStorage.getItem("hw_params") || sessionStorage.getItem("dt_params"))
  );

  const handleStart = () => {
    sessionStorage.setItem("returnTo", "/test-modes");
    navigate("/vocab-test/game", {
      state: { testType, wordCount, tier: selectedTier },
    });
  };

  return (
    <div className="vts-page">
      <PageHeader pageTitle="Test Setup" pageSubtitle="Configure your vocabulary test" />

      <div className="vts-body">
        {/* Test type badge */}
        <div className={`vts-type-badge ${meta.color}`}>
          {meta.icon}
          <span>{meta.label}</span>
        </div>

        {/* Word count */}
        <section className="vts-section">
          <div className="vts-section-header">
            <Hash size={18} />
            <h2>Number of questions</h2>
          </div>
          <div className="vts-options">
            {WORD_COUNTS.map((n) => (
              <button
                key={n}
                className={`vts-option-btn ${wordCount === n ? "active" : ""}`}
                onClick={() => setWordCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </section>

        {/* Year group selector */}
        <section className="vts-section">
          <div className="vts-section-header">
            <GraduationCap size={18} />
            <h2>Year group</h2>
          </div>
          <select
            className="vts-year-select"
            value={selectedTier}
            disabled={lockedTier}
            onChange={(e) => setSelectedTier(parseInt(e.target.value, 10))}
          >
            {DISPLAY_YEAR_OPTIONS.map((opt) => (
              <option key={opt.tier} value={opt.tier}>
                {opt.label}
              </option>
            ))}
          </select>
        </section>

        {/* Info note */}
        <p className="vts-note">
          Questions are weighted toward your weakest words so you improve faster.
        </p>

        {/* Actions */}
        <div className="vts-actions">
          <button className="vts-back-btn" onClick={() => navigate("/test-modes")}>
            <ArrowLeft size={15} /> Back
          </button>
          <button className="vts-start-btn" onClick={handleStart}>
            Start Test →
          </button>
        </div>
      </div>
    </div>
  );
}
